import { useRef, useState } from 'react';
import type { AttachmentKind, CampaignAttachment } from '../types';
import { MAX_ATTACHMENT_BYTES, formatFileSize, getAttachmentKind } from '../helpers';

interface BriefAttachmentsProps {
  attachments: CampaignAttachment[];
  onChange: (attachments: CampaignAttachment[]) => void;
}

interface KindMeta {
  label: string;
  icon: string;
  tone: string;
  hint: string;
}

const kindMeta: Record<AttachmentKind, KindMeta> = {
  document: {
    label: 'Document',
    icon: 'ri-file-text-line',
    tone: 'bg-secondary-100 text-secondary-700',
    hint: 'PDF, DOC, PPT, XLS, TXT, CSV',
  },
  image: {
    label: 'Image',
    icon: 'ri-image-2-line',
    tone: 'bg-accent-100 text-accent-700',
    hint: 'JPG, PNG, GIF, WEBP',
  },
  video: {
    label: 'Video',
    icon: 'ri-movie-2-line',
    tone: 'bg-primary-100 text-primary-700',
    hint: 'MP4, MOV, WEBM',
  },
  audio: {
    label: 'Audio',
    icon: 'ri-mic-line',
    tone: 'bg-secondary-100 text-secondary-700',
    hint: 'MP3, WAV, M4A',
  },
};

const acceptAttr =
  'image/*,video/*,audio/*,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.md,.rtf,.odt,.ods,.odp';

let attachmentSeq = 0;

export default function BriefAttachments({ attachments, onChange }: BriefAttachmentsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const accepted: CampaignAttachment[] = [];
    const rejected: string[] = [];

    Array.from(fileList).forEach((file) => {
      const kind = getAttachmentKind(file.type, file.name);
      if (!kind) {
        rejected.push(`${file.name} — unsupported format`);
        return;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        rejected.push(`${file.name} — over 50 MB`);
        return;
      }
      attachmentSeq += 1;
      accepted.push({
        id: `att-${Date.now()}-${attachmentSeq}`,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        kind,
        file,
        previewUrl: kind === 'image' ? URL.createObjectURL(file) : null,
      });
    });

    if (accepted.length > 0) {
      onChange([...attachments, ...accepted]);
    }
    setNotice(rejected.length > 0 ? `Skipped ${rejected.join('; ')}` : null);
  };

  const removeFile = (file: CampaignAttachment) => {
    if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
    onChange(attachments.filter((item) => item.id !== file.id));
  };

  const totalSize = attachments.reduce((sum, file) => sum + file.size, 0);

  return (
    <div className="border-t border-background-200 px-5 py-5">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50 text-base">
          <i className="ri-attachment-2" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Attachments
          </h3>
          <p className="text-xs text-foreground-500">
            Share briefs, mood boards and references — documents, images, video or audio.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-8 text-center transition-colors cursor-pointer ${
          dragging
            ? 'border-primary-400 bg-primary-50'
            : 'border-background-300 bg-background-100 hover:border-primary-300 hover:bg-background-200/60'
        }`}
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xl">
          <i className="ri-upload-cloud-2-line" />
        </span>
        <span className="text-sm font-medium text-foreground-800">
          Click to upload or drag and drop
        </span>
        <span className="text-xs text-foreground-500">
          Documents, images, video or audio · up to 50 MB per file
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptAttr}
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {notice ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-accent-700">
          <i className="ri-error-warning-line" />
          {notice}
        </p>
      ) : null}

      {attachments.length > 0 ? (
        <>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs font-label font-medium text-foreground-700">
              {attachments.length === 1 ? '1 file attached' : `${attachments.length} files attached`}
            </p>
            <p className="text-[11px] text-foreground-400">{formatFileSize(totalSize)} total</p>
          </div>
          <ul className="mt-2 space-y-2">
            {attachments.map((file) => {
              const meta = kindMeta[file.kind];
              return (
                <li
                  key={file.id}
                  className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
                >
                  {file.previewUrl ? (
                    <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-background-200">
                      <img
                        src={file.previewUrl}
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                    </span>
                  ) : (
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-lg ${meta.tone}`}
                    >
                      <i className={meta.icon} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-800">
                      {file.name}
                    </p>
                    <p className="truncate text-[11px] text-foreground-500">
                      {meta.label} · {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(file)}
                    aria-label={`Remove ${file.name}`}
                    className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <i className="ri-close-line" />
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="mt-3 text-center text-xs text-foreground-400">
          No files attached yet. You can also add more once the campaign is created.
        </p>
      )}
    </div>
  );
}